import React, { useCallback, useMemo, useState } from 'react'

import { gql } from '@apollo/client'
import classNames from 'classnames'

import { CommonVerifiedIcon } from '@local/assets/icons'

import Clickable from '@web/components/Clickable'
import ContextMenuFollow from '@web/components/ContextMenuFollow'
import FollowAcceptOrRejectButton from '@web/components/FollowAcceptOrRejectButton'
import FollowButton, { FollowButtonProps } from '@web/components/FollowButton'
import ProfileThumbnail from '@web/components/ProfileThumbnail'
import { UserRelationshipItem_UserFragment, UserRelationshipType } from '@web/gql/graphql'
import useBlockModal from '@web/hooks/useBlockModal'
import { useGoTo } from '@web/hooks/useGoTo'
import useMe from '@web/hooks/useMe'
import usePageInfo from '@web/hooks/usePageInfo'
import routes from '@web/libs/constants/routes'
import nClicks from '@web/libs/nlog/click/codes'

import styles from './UserRelationshipItem.module.scss'

interface Props {
  user: UserRelationshipItem_UserFragment
  type: UserRelationshipType
}

const UserRelationshipItem = ({ type, user }: Props) => {
  const { me } = useMe()
  const { isMyPage } = usePageInfo()
  const { goTo } = useGoTo()
  const { openUnBlockModal, openBlockModal } = useBlockModal({ user })

  const isMe = me?.id === user.id
  const isBlocking = type === UserRelationshipType.Block

  const [deleted, setDeleted] = useState(false)
  const [accepted, setAccepted] = useState(false)

  const onClickProfile = () => goTo(routes.HOME_REVIEW, { params: { userId: user.id }, openWindow: true })

  const onDelete = useCallback(() => setDeleted(true), [setDeleted])
  const onCompleteAccept = useCallback(() => setAccepted(true), [setAccepted])

  const profileClickCode = useMemo<string | undefined>(() => {
    const typeAreaMap = {
      [UserRelationshipType.Follower]: (isMyPage ? nClicks.followListMy : nClicks.followListYour).FOLLOWERPROFILE,
      [UserRelationshipType.Following]: (isMyPage ? nClicks.followListMy : nClicks.followListYour).FOLLOWINGPROFILE,
      [UserRelationshipType.Block]: isMyPage ? nClicks.followListMy.BLOCKPROFILE : undefined,
      [UserRelationshipType.FollowRequest]: isMyPage ? nClicks.followListMy.REQPROFILE : undefined,
      [UserRelationshipType.FollowRequester]: isMyPage ? nClicks.followListMy.REQPROFILE : undefined,
    } as const

    return typeAreaMap[type]
  }, [type, isMyPage])

  const followButtonClickCodes = useMemo<FollowButtonProps['nClickCodes']>(() => {
    const typeAreaMap = {
      [UserRelationshipType.Follower]: isMyPage ? nClicks.followListMyFollower : nClicks.followListYourFollower,
      [UserRelationshipType.Following]: isMyPage ? nClicks.followListMyFollowing : nClicks.followListYourFollowing,
      [UserRelationshipType.Block]: isMyPage ? nClicks.followListMyBlocking : undefined,
      [UserRelationshipType.FollowRequest]: undefined,
      [UserRelationshipType.FollowRequester]: undefined,
    } as const

    const area = typeAreaMap[type]
    if (!area) return {}

    return {
      cancelFollow: area.UNFOLLOW,
      cancelFollowReqeust: area.FOLLOWREQUESTCANCEL,
      followRequest: area.FOLLOW,
    }
  }, [type, isMyPage])

  return (
    <li
      className={classNames(styles.list_item, {
        [styles.type_request]: !accepted && type === UserRelationshipType.FollowRequester,
        [styles.hide]: deleted,
      })}
    >
      <Clickable className={styles.thumb_area} onClick={onClickProfile} nClickCode={profileClickCode}>
        <ProfileThumbnail size={36} noBorder thumbnailUrl={user.profile?.originImageUrl} />
      </Clickable>
      <div className={styles.tit_area}>
        <Clickable className={styles.inner} onClick={onClickProfile} nClickCode={profileClickCode}>
          {user.profile.nickname}
          {user.isThemeContentProvider && <CommonVerifiedIcon className={styles.icon} aria-hidden="true" />}
        </Clickable>
      </div>
      {!isMe && (
        <div className={styles.btn_area}>
          {isBlocking && (
            <>
              {user.isBlocked ? (
                <Clickable
                  className={classNames(styles.btn, styles.type_unblock)}
                  onClick={() => openUnBlockModal()}
                  nClickCode={nClicks.followListMy.REVIEWERUNBLOCK}
                >
                  차단 해제
                </Clickable>
              ) : (
                <Clickable
                  className={classNames(styles.btn, styles.type_block)}
                  onClick={() => openBlockModal()}
                  nClickCode={nClicks.followListMy.REVIEWERBLOCK}
                >
                  리뷰어 차단
                </Clickable>
              )}
            </>
          )}
          {!isBlocking && (
            <>
              {type === UserRelationshipType.FollowRequester ? (
                <FollowAcceptOrRejectButton
                  user={user}
                  onCompleteDelete={onDelete}
                  onCompleteAccept={onCompleteAccept}
                />
              ) : (
                <FollowButton user={user} nClickCodes={followButtonClickCodes} />
              )}
              {isMyPage && (
                <div className={styles.btn_manage_wrapper}>
                  <ContextMenuFollow
                    showDeleteMenu={type === UserRelationshipType.Follower}
                    user={user}
                    onDeleteFollower={onDelete}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  )
}

UserRelationshipItem.fragments = {
  user: gql`
    fragment UserRelationshipItem_User on User {
      id
      profile {
        nickname
        originImageUrl
      }
      isBlocked
      isThemeContentProvider
      ...UseBlockModal_User
      ...FollowButton_User
      ...UseFollowMethods_Other
    }
  `,
}
export default UserRelationshipItem
